import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Building, Tag, Clock, User, Share2, MapPin, Flame, Globe, Edit, Printer, Users, DollarSign, MessageSquare, ExternalLink, Zap, ZapOff, CalendarDays, Pencil, Briefcase, Factory, Smartphone, Sparkles, Loader2, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MemberAvatar } from '@/components/MemberAvatar';
import { FaceTimeButtons } from '@/components/lead-detail/FaceTimeButtons';
import { FaceTimeInviteDialog } from '@/components/lead-detail/FaceTimeInviteDialog';
import { HunterEmailActions } from '@/components/lead-detail/HunterEmailActions';
import { statusColor, leadAge } from '@/components/leads/constants';
import api from '@/lib/api';
import { toast } from 'sonner';

export function LeadInfoCard({ lead, isViewer, onEdit, onShare, onPrint, onEmail, onSnap, onRefresh }) {
  const [editingBsd, setEditingBsd] = useState(false);
  const [bsdValue, setBsdValue] = useState(lead.business_start_date || '');
  const [enriching, setEnriching] = useState(false);
  const [showFaceTimeInvite, setShowFaceTimeInvite] = useState(false);

  const handleSaveBsd = async (val) => {
    try {
      await api.updateLead(lead.id, { business_start_date: val || null });
      toast.success('Business Start Date updated');
      setEditingBsd(false);
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const handleEnrich = async () => {
    if (enriching) return;
    setEnriching(true);
    try {
      const { data } = await api.seamlessSyncEnrich(lead.id);
      if (!data.enriched) {
        toast.info('No matching Seamless.AI record found', { description: data.message });
      } else if (data.filled_fields?.length) {
        toast.success(`Enriched with ${data.filled_fields.length} new field${data.filled_fields.length === 1 ? '' : 's'}`, {
          description: data.filled_fields.join(', '),
        });
      } else {
        toast.info('Already up to date', { description: 'No missing fields to fill — metadata refreshed.' });
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Enrichment failed';
      toast.error('Could not enrich from Seamless.AI', { description: msg });
    } finally {
      setEnriching(false);
    }
  };

  const fullAddress = useMemo(() =>
    [lead.address, lead.city, lead.state, lead.zip_code].filter(Boolean).join(', '),
    [lead.address, lead.city, lead.state, lead.zip_code]
  );
  const displayAddress = useMemo(() =>
    [lead.address, lead.city, lead.state ? `${lead.state} ${lead.zip_code || ''}`.trim() : lead.zip_code].filter(Boolean).join(', '),
    [lead.address, lead.city, lead.state, lead.zip_code]
  );

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="lead-name">{lead.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-sm ${statusColor(lead.status)}`} data-testid="lead-status">{lead.status}</span>
            {lead.snapped_by && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-sm bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" data-testid="snapped-badge">
                <Zap className="w-3 h-3" /> Snapped
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isViewer && (
            <Button variant="outline" className="rounded-sm text-sm" onClick={onEdit} data-testid="edit-lead-btn">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
          {!isViewer && (lead.email || (lead.name && lead.company)) && (
            <Button
              variant="outline"
              className="rounded-sm text-sm border-violet-500 text-violet-700 hover:bg-violet-50 disabled:opacity-60"
              onClick={handleEnrich}
              disabled={enriching}
              data-testid="enrich-lead-btn"
              title="Pull fresh title, department, industry, SIC/NAICS, and contact details from Seamless.AI"
            >
              {enriching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {enriching ? 'Refreshing…' : 'Refresh Lead'}
            </Button>
          )}
          {!isViewer && (
            <Button variant="outline" className="rounded-sm text-sm border-blue-500 text-blue-600 hover:bg-blue-50" onClick={onShare} data-testid="share-lead-btn">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
          )}
          {!isViewer && lead.email && (
            <Button variant="outline" className="rounded-sm text-sm border-amber-500 text-amber-600 hover:bg-amber-50" onClick={onEmail} data-testid="email-lead-btn">
              <Mail className="w-4 h-4 mr-1" /> Email
            </Button>
          )}
          {!isViewer && (
            <Button
              variant={lead.snapped_by ? "default" : "outline"}
              className={`rounded-sm text-sm ${lead.snapped_by ? 'bg-red-500 hover:bg-red-600 text-white border-red-500' : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'}`}
              onClick={onSnap}
              data-testid="snap-lead-btn"
            >
              {lead.snapped_by ? <><ZapOff className="w-4 h-4 mr-1" /> Unsnap</> : <><Zap className="w-4 h-4 mr-1" /> Snap</>}
            </Button>
          )}
          <Button variant="default" className="rounded-sm text-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={onPrint} data-testid="print-lead-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        {(lead.title || lead.department || lead.seniority) && (
          <div className="flex items-center gap-3 sm:col-span-2" data-testid="lead-role">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Briefcase className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Role</p>
              <p className="text-sm font-medium text-slate-900">
                {[lead.title, lead.department, lead.seniority].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        )}
        {lead.email && (
          <div className="flex items-center gap-3" data-testid="lead-email">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Mail className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Primary Email</p>
              <a href={`mailto:${lead.email}`} className="text-sm font-medium text-[#0047AB] hover:underline" data-testid="click-to-email-btn">{lead.email}</a>
              <HunterEmailActions lead={lead} onRefresh={onRefresh} isViewer={isViewer} />
            </div>
          </div>
        )}
        {!lead.email && !isViewer && lead.name && (lead.website || lead.company) && (
          <div className="flex items-center gap-3" data-testid="lead-email-empty">
            <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center"><Mail className="w-4 h-4 text-slate-400" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Primary Email</p>
              <p className="text-sm text-slate-400 italic">No email on file</p>
              <HunterEmailActions lead={lead} onRefresh={onRefresh} isViewer={isViewer} />
            </div>
          </div>
        )}
        {lead.alternate_email && (
          <div className="flex items-center gap-3" data-testid="lead-alt-email">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Mail className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Alternate Email</p>
              <a href={`mailto:${lead.alternate_email}`} className="text-sm font-medium text-[#0047AB] hover:underline">{lead.alternate_email}</a>
            </div>
          </div>
        )}
        {lead.phone && (
          <div className="flex items-center gap-3" data-testid="lead-phone">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Phone className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Primary Number</p>
              <a href={`tel:${lead.phone}`} onClick={() => api.logLeadTouch(lead.id, 'call')} className="text-sm font-medium text-[#0047AB] hover:underline flex items-center gap-1.5" data-testid="click-to-call-btn">
                {lead.phone}
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium px-1.5 py-0.5 bg-[#EFF6FF] text-[#0047AB] rounded-sm">Call</span>
              </a>
              <a href={`sms:${lead.phone}`} onClick={() => api.logLeadTouch(lead.id, 'sms')} className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.15em] font-medium px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm hover:bg-emerald-100 transition-colors" data-testid="click-to-text-btn">
                <MessageSquare className="w-3 h-3" /> Text
              </a>
              <FaceTimeButtons contact={lead.phone} size="compact" className="mt-1 ml-1" />
              {!isViewer && (lead.phone || lead.email) && (
                <button
                  type="button"
                  onClick={() => setShowFaceTimeInvite(true)}
                  className="inline-flex items-center gap-1 mt-1 ml-1 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] font-medium rounded-sm border border-[#5856D6]/40 text-[#5856D6] bg-[#5856D6]/5 hover:bg-[#5856D6]/10 transition-colors"
                  title="Email or text the lead a FaceTime link"
                  data-testid="facetime-invite-launcher"
                >
                  Invite
                </button>
              )}
              {!isViewer && lead.phone && (
                <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm border border-[#0047AB]/30 text-[#0047AB] hover:bg-[#EFF6FF] transition-colors" data-testid="lead-tel-link">
                  <Phone className="w-3 h-3" /> Call
                </a>
              )}
            </div>
          </div>
        )}
        {lead.mobile_number && (
          <div className="flex items-center gap-3" data-testid="lead-mobile">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Smartphone className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Mobile Number</p>
              <a href={`tel:${lead.mobile_number}`} onClick={() => api.logLeadTouch(lead.id, 'call')} className="text-sm font-medium text-[#0047AB] hover:underline flex items-center gap-1.5">
                {lead.mobile_number}
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium px-1.5 py-0.5 bg-[#EFF6FF] text-[#0047AB] rounded-sm">Call</span>
              </a>
              <a href={`sms:${lead.mobile_number}`} onClick={() => api.logLeadTouch(lead.id, 'sms')} className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.15em] font-medium px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm hover:bg-emerald-100 transition-colors">
                <MessageSquare className="w-3 h-3" /> Text
              </a>
              <FaceTimeButtons contact={lead.mobile_number} size="compact" className="mt-1 ml-1" />
            </div>
          </div>
        )}
        {lead.company && (
          <div className="flex items-center gap-3" data-testid="lead-company">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Building className="w-4 h-4 text-[#0047AB]" /></div>
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">Company Name</p><p className="text-sm font-medium text-slate-900">{lead.company}</p></div>
          </div>
        )}
        {lead.website && (
          <div className="flex items-center gap-3" data-testid="lead-website">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><ExternalLink className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Website</p>
              <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#0047AB] hover:underline" data-testid="lead-website-link">{lead.website}</a>
            </div>
          </div>
        )}
        {lead.category && (
          <div className="flex items-center gap-3" data-testid="lead-category">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Tag className="w-4 h-4 text-[#0047AB]" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Category</p>
              <p className="text-sm font-medium text-slate-900">{lead.category}{lead.sub_category ? <span className="text-slate-500 font-normal"> / {lead.sub_category}</span> : ''}</p>
            </div>
          </div>
        )}
        {lead.lead_source && (
          <div className="flex items-center gap-3" data-testid="lead-source">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Globe className="w-4 h-4 text-[#0047AB]" /></div>
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">Source</p><p className="text-sm font-medium text-slate-900">{lead.lead_source}</p></div>
          </div>
        )}
        {lead.number_of_employees != null && (
          <div className="flex items-center gap-3" data-testid="lead-employees">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Users className="w-4 h-4 text-[#0047AB]" /></div>
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">Size (Employees)</p><p className="text-sm font-medium text-slate-900">{lead.number_of_employees.toLocaleString()}</p></div>
          </div>
        )}
        {lead.annual_revenue != null && (
          <div className="flex items-center gap-3" data-testid="lead-revenue">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><DollarSign className="w-4 h-4 text-[#0047AB]" /></div>
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">Revenue</p><p className="text-sm font-medium text-slate-900">${lead.annual_revenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p></div>
          </div>
        )}
        {(lead.industry || lead.sic_code || lead.naics_code) && (
          <div className="flex items-center gap-3 sm:col-span-2" data-testid="lead-industry">
            <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><Factory className="w-4 h-4 text-[#0047AB]" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Industry</p>
              <p className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                {lead.industry && <span>{lead.industry}</span>}
                {lead.sic_code && <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">SIC {lead.sic_code}</span>}
                {lead.naics_code && <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">NAICS {lead.naics_code}</span>}
              </p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3" data-testid="lead-business-start">
          <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center"><CalendarDays className="w-4 h-4 text-[#0047AB]" /></div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Business Start Date</p>
            {editingBsd ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Input type="date" value={bsdValue} onChange={e => setBsdValue(e.target.value)} className="rounded-sm h-7 text-sm w-36" data-testid="edit-bsd-input" />
                <Button size="sm" className="h-7 px-2 bg-[#0047AB] hover:bg-[#003080] text-white rounded-sm text-xs" onClick={() => handleSaveBsd(bsdValue)} data-testid="save-bsd-btn">Save</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 rounded-sm text-xs" onClick={() => { setEditingBsd(false); setBsdValue(lead.business_start_date || ''); }}>Cancel</Button>
              </div>
            ) : (
              <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                {lead.business_start_date ? new Date(lead.business_start_date).toLocaleDateString() : <span className="text-slate-400">Not set</span>}
                {!isViewer && <button onClick={() => { setBsdValue(lead.business_start_date || ''); setEditingBsd(true); }} className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 text-[10px] font-medium text-[#0047AB] bg-[#EFF6FF] border border-[#0047AB]/20 rounded-sm hover:bg-blue-100 transition-colors" data-testid="edit-bsd-btn"><Pencil className="w-3 h-3" /> Edit</button>}
              </p>
            )}
          </div>
          <div className="ml-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Years in Business</p>
            {lead.business_start_date ? (() => {
              const years = Math.max(0, (Date.now() - new Date(lead.business_start_date).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
              return <p className="text-sm font-bold text-[#0047AB]" data-testid="years-in-business">{years < 1 ? '<1' : Math.floor(years)}</p>;
            })() : <p className="text-sm text-slate-400" data-testid="years-in-business">---</p>}
          </div>
        </div>
      </div>
      {(lead.address || lead.city || lead.state) && (
        <div className="mt-4 flex items-center gap-3" data-testid="lead-address">
          <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-[#0047AB]" /></div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Address</p>
            <p className="text-sm font-medium text-slate-900">
              {displayAddress}
            </p>
          </div>
        </div>
      )}
      {(lead.address || lead.city || lead.state) && (
        <div className="mt-4 rounded-sm overflow-hidden border border-slate-200" data-testid="lead-map">
          <iframe
            title="Lead Location"
            width="100%"
            height="260"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(fullAddress)}&t=k&z=17&ie=UTF8&iwloc=&output=embed`}
          />
          <div className="flex items-stretch border-t border-slate-200 bg-slate-50 divide-x divide-slate-200">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-[#0047AB] hover:bg-blue-50 transition-colors"
              data-testid="open-in-google-maps"
            >
              <MapPin className="w-3 h-3" /> Open in Maps
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#0047AB] hover:bg-[#003580] transition-colors"
              data-testid="get-directions-btn"
            >
              <Navigation className="w-3 h-3" /> Get directions
            </a>
          </div>
        </div>
      )}
      {lead.tags?.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="lead-tags">
          <span className="text-xs text-slate-500 uppercase tracking-wide">Tags:</span>
          {lead.tags.map((t) => (
            <span key={t} className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{t}</span>
          ))}
        </div>
      )}
      {lead.shared_with_names?.length > 0 && (
        <div className="mt-4" data-testid="lead-shared-with">
          <div className="flex items-center gap-1.5 mb-2">
            <Share2 className="w-3.5 h-3.5 text-[#0047AB]" />
            <span className="text-xs text-slate-500 uppercase tracking-wide">Shared with</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {lead.shared_with?.map((uid, i) => (
              <div key={uid} className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded-sm">
                <MemberAvatar userId={uid} name={lead.shared_with_names?.[i]} size="xs" />
                <span className="text-xs font-medium text-slate-700">{lead.shared_with_names?.[i]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {lead.created_by_name}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleDateString()}</span>
        {(() => { const age = leadAge(lead.created_at, lead.status, lead.closed_at); return (
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${age.cls}`} data-testid="lead-age-badge">
            <Clock className="w-3 h-3" />{age.label}
          </span>
        ); })()}
      </div>
      <FaceTimeInviteDialog
        open={showFaceTimeInvite}
        onClose={() => setShowFaceTimeInvite(false)}
        lead={lead}
      />
    </div>
  );
}
